/**
 * How a campaign's audience reads back to the merchant (round 9, TP-7 and TP-15).
 *
 * The active-campaign panel wrote its own summaries and got three of them wrong, in a
 * way no amount of looking at the screen would explain: it read `targeting.venue_type`
 * and `targeting.age_range` while CampaignTargeting has carried `venue_types` and
 * `age_ranges` since they became multi-select. Both were therefore always undefined,
 * so the panel printed "Any" and "All ages" for every campaign ever made, whatever the
 * merchant had chosen. `interests` had the right name but is a list, and a list
 * rendered straight into JSX comes out as "cafesbars".
 *
 * Joel filed it as wording. It is not wording, and that is why these live in one
 * module instead of inline at the call site: the form and the summary now read the
 * same fields through the same functions, so the next rename breaks both or neither.
 *
 * A read-only summary NAMES the selections rather than counting them. "3 types
 * selected" is right on a control, because clicking it shows which; on a panel there
 * is nothing to click.
 */

// Stored sentinels meaning "the merchant chose nothing", which is not the same as a
// choice and must not be displayed as one (round 7, SP-19).
const SENTINELS = new Set(["any", "all"]);

export const NONE_SELECTED = "None selected";

/** Title Case from a stored code: "shopping_centre" becomes "Shopping Centre". */
export function prettyValue(value) {
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The values a merchant actually picked, with the sentinels and blanks removed. */
export function chosenValues(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((v) => v && !SENTINELS.has(String(v).toLowerCase()));
}

/**
 * Name up to `limit` selections, then say how many more. Falls back to `empty` when
 * nothing was chosen.
 */
export function summariseList(list, { empty = NONE_SELECTED, limit = 3, labels = null } = {}) {
  const chosen = chosenValues(list);
  if (chosen.length === 0) return empty;
  const name = (v) => (labels && labels[v]) || prettyValue(v);
  const shown = chosen.slice(0, limit).map(name).join(", ");
  return chosen.length > limit ? `${shown} and ${chosen.length - limit} more` : shown;
}

// --- the individual cells, each named so a call site reads as what it shows ---------

export const summariseVenueTypes = (t = {}) => summariseList(t.venue_types);

export const summariseInterests = (t = {}) => summariseList(t.interests);

export const summariseAges = (t = {}) => summariseList(t.age_ranges, { empty: "All ages" });

export const summariseGender = (t = {}) => {
  const g = t.gender;
  return !g || SENTINELS.has(String(g).toLowerCase()) ? "All" : prettyValue(g);
};

export const summariseDays = (t = {}) => {
  const days = Array.isArray(t.days) ? t.days : [];
  if (days.length === 0 || days.length === 7) return "All days";
  return summariseList(days.map((d) => String(d).slice(0, 3)), { limit: 7 });
};

export const summariseTimes = (t = {}) => {
  const slots = Array.isArray(t.time_slots) ? t.time_slots : [];
  if (slots.length === 0 || slots.length === 4) return "All day";
  return summariseList(slots, { limit: 4 });
};

export const summariseRadius = (t = {}) =>
  t.radius_override ? `${t.radius_override}m` : "Default";

/**
 * Locations are counted rather than named: a campaign can carry dozens of postcodes
 * and suburbs, and the merchant chose them through a dedicated screen that shows them.
 */
export const summariseLocations = (t = {}) => {
  const parts = [];
  const add = (list, singular) => {
    const n = (list || []).length;
    if (n) parts.push(`${n} ${singular}${n > 1 ? "s" : ""}`);
  };
  add(t.location_ids, "venue");
  add(t.states, "state");
  add(t.postcodes, "postcode");
  add(t.suburbs, "suburb");
  return parts.length ? parts.join(", ") : "All areas";
};
